import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Global axios: send cookies AND Bearer token for cross-site compatibility
axios.defaults.withCredentials = true;

// In-memory token store (not localStorage — XSS safe)
let _memoryToken = null;

// Axios interceptor: attach Bearer token to every request
axios.interceptors.request.use((config) => {
  if (_memoryToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${_memoryToken}`;
  }
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialCheckDone = useRef(false);

  // Hydrate user on mount — cookie OR token
  useEffect(() => {
    let cancelled = false;
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/auth/me`);
        if (!cancelled) setUser(response.data);
      } catch {
        if (!cancelled) {
          setUser(null);
          _memoryToken = null;
        }
      }
      if (!cancelled) {
        setLoading(false);
        initialCheckDone.current = true;
      }
    };
    checkAuth();
    return () => { cancelled = true; };
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password,
      });
      const { user: userData, access_token } = response.data;
      // Store token in memory for Bearer header fallback
      if (access_token) {
        _memoryToken = access_token;
      }
      setUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      const message = error.response?.data?.detail || "Giris basarisiz";
      return { success: false, error: message };
    }
  };

  const register = async (data) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/register`, data);
      const { user: userData, access_token } = response.data;
      if (access_token) {
        _memoryToken = access_token;
      }
      setUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      const message = error.response?.data?.detail || "Kayit basarisiz";
      return { success: false, error: message };
    }
  };

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`);
    } catch {
      // Ignore — cookie may already be gone
    }
    _memoryToken = null;
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
      return response.data;
    } catch {
      return null;
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        refreshUser,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
